import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildLoginPageInnerHtml, loginStageVideo, resolveLoginClip } from '../constants/loginPageWidget';

/**
 * O vídeo do palco fica fora do HTML que o editor recria.
 * No visualizar e no Moodle o player entra no HTML publicado e não pisca.
 */
const StageVideo = memo(function StageVideo({ kind, src, poster, clip }) {
  const frameRef = useRef(null);
  const start = Math.max(0, Number(clip?.start) || 0);
  const end = Number(clip?.end) > start ? Number(clip.end) : 0;

  useEffect(() => {
    const frame = frameRef.current;
    if (kind !== 'iframe' || !frame || !(end > start)) return undefined;
    const arm = () => {
      try {
        frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 'madri-login-stage', channel: 'widget' }), '*');
      } catch (_) { /* o player ainda não aceita comando */ }
    };
    const onMessage = (event) => {
      if (!frame.contentWindow || event.source !== frame.contentWindow) return;
      let msg = event.data;
      if (typeof msg === 'string') {
        try { msg = JSON.parse(msg); } catch (_) { return; }
      }
      if (!msg || Number(msg.info) !== 0) return;
      try {
        frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'seekTo', args: [start, true] }), '*');
        frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
      } catch (_) { /* ignora se o iframe ainda não respondeu */ }
    };
    frame.addEventListener('load', arm);
    window.addEventListener('message', onMessage);
    return () => {
      frame.removeEventListener('load', arm);
      window.removeEventListener('message', onMessage);
    };
  }, [kind, src, start, end]);

  if (kind === 'iframe') {
    return (
      <div className="madri-login__streamwrap">
        <iframe ref={frameRef} className="madri-login__stream" src={src} title="" tabIndex={-1} allow="autoplay; encrypted-media" />
      </div>
    );
  }
  if (kind === 'video') {
    const jump = (node) => {
      if (end > start && (node.currentTime >= end || node.currentTime + 0.05 < start)) node.currentTime = start;
    };
    return (
      <video
        className="madri-login__bg"
        src={src}
        poster={poster || undefined}
        muted
        autoPlay
        playsInline
        onLoadedMetadata={(event) => jump(event.currentTarget)}
        onTimeUpdate={(event) => jump(event.currentTarget)}
        onEnded={(event) => {
          const node = event.currentTarget;
          if (end > start) node.currentTime = start;
          const again = node.play();
          if (again && again.catch) again.catch(() => {});
        }}
      />
    );
  }
  return null;
});

/**
 * Palco da página de login. A largura segue a folha do curso (boxed ou total),
 * sem esticar até a área cinza ao redor do palco.
 * above/below são grids editáveis dentro da página, para a altura crescer com o fundo.
 */
export default function RenderSiteLogin({ content = {}, widgetId, above = null, below = null }) {
  const slotRef = useRef(null);
  const [bleed, setBleed] = useState(null);
  const [videoSlot, setVideoSlot] = useState(null);
  const [aboveSlot, setAboveSlot] = useState(null);
  const [belowSlot, setBelowSlot] = useState(null);
  const contentKey = JSON.stringify(content || {});
  const html = useMemo(
    () => buildLoginPageInnerHtml(content, widgetId, { stage: true }),
    [contentKey, widgetId]
  );
  const clip = resolveLoginClip(content);
  const video = loginStageVideo(content.videoUrl, clip);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const host = slot?.parentElement;
    const canvas = slot?.closest('.course-wrapper');
    if (!host || !canvas) return undefined;
    const measure = () => {
      const hostBox = host.getBoundingClientRect();
      const canvasBox = canvas.getBoundingClientRect();
      const width = Math.round(canvasBox.width);
      const marginLeft = Math.round(canvasBox.left - hostBox.left);
      setBleed((prev) => {
        if (prev && Math.abs(prev.width - width) < 2 && Math.abs(prev.marginLeft - marginLeft) < 2) return prev;
        return { width, marginLeft };
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useLayoutEffect(() => {
    const root = slotRef.current;
    setVideoSlot(root?.querySelector('[data-gp-login-stage-video]') || null);
    setAboveSlot(root?.querySelector('[data-gp-login-above]') || null);
    setBelowSlot(root?.querySelector('[data-gp-login-below]') || null);
  }, [html]);

  return (
    <>
      <div
        ref={slotRef}
        style={bleed ? { width: bleed.width, marginLeft: bleed.marginLeft, maxWidth: 'none' } : undefined}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {videoSlot && video.src
        ? createPortal(
          <StageVideo kind={video.kind} src={video.src} poster={content.imageUrl || ''} clip={clip} />,
          videoSlot
        )
        : null}
      {aboveSlot && above ? createPortal(above, aboveSlot) : null}
      {belowSlot && below ? createPortal(below, belowSlot) : null}
    </>
  );
}
